import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const ContactosPage = () => {
  const [conteudo, setConteudo] = useState<any>(null);
  const [contactosInfo, setContactosInfo] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    assunto: '',
    mensagem: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    carregarConteudo();
    carregarContactosInfo();
  }, []);

  const carregarConteudo = async () => {
    const { data } = await supabase
      .from('conteudo_site')
      .select('*')
      .eq('secao', 'contactos')
      .single();

    if (data) {
      setConteudo(data);
    }
  };

  const carregarContactosInfo = async () => {
    const { data } = await supabase
      .from('contactos_info')
      .select('*')
      .eq('ativo', true)
      .order('ordem');

    if (data) {
      setContactosInfo(data);
    }
  };

  const getContactoByType = (tipo: string) => {
    return contactosInfo.find(item => item.tipo === tipo)?.valor || '';
  };

  const getContactosVisibles = () => {
    return contactosInfo.filter(item => item.ativo);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome.trim() || !formData.email.trim() || !formData.assunto || !formData.mensagem.trim()) {
      setSubmitStatus('error');
      return;
    }

    if (formData.mensagem.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const emailDestino = getContactoByType('email') || 'dindimcast@gmail.com';
      
      const response = await fetch('https://readdy.ai/api/form/d4scsd91e6in3mfvkeag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          ...formData,
          email_destino: emailDestino
        } as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          assunto: '',
          mensagem: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIconForType = (tipo: string) => {
    const icons: { [key: string]: string } = {
      email: 'ri-mail-line',
      telefone: 'ri-phone-line',
      endereco: 'ri-map-pin-line',
      horario_semana: 'ri-time-line',
      horario_sabado: 'ri-calendar-line',
      horario_domingo: 'ri-calendar-line',
      instagram: 'ri-instagram-line',
    };
    return icons[tipo] || 'ri-information-line';
  };

  const renderSocialIcon = (tipo: string) => {
    return <i className={`${getIconForType(tipo)} text-lg`}></i>;
  };

  const isRedesSociais = (tipo: string) => {
    return ['instagram'].includes(tipo);
  };

  const isHorario = (tipo: string) => {
    return ['horario_semana', 'horario_sabado', 'horario_domingo'].includes(tipo);
  };

  const redesSociais = contactosInfo.filter(item => isRedesSociais(item.tipo));
  const horarios = contactosInfo.filter(item => isHorario(item.tipo));
  const outrosContactos = contactosInfo.filter(item => !isRedesSociais(item.tipo) && !isHorario(item.tipo));

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />

      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="px-8 lg:px-20 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-white text-5xl lg:text-7xl font-serif font-bold mb-6">
              <span className="bg-gradient-gold bg-clip-text text-transparent">
                {conteudo?.titulo || 'Entre em Contacto'}
              </span>
            </h1>
            <p className="text-gray-400 text-lg lg:text-xl max-w-3xl mx-auto">
              {conteudo?.subtitulo || 'Estamos aqui para ajudar. Envie sua mensagem e responderemos em breve'}
            </p>
          </motion.div>
        </section>

        <div className="px-8 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Formulário */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contacto-form">
                <div>
                  <label htmlFor="nome" className="block text-luxury-silver text-sm mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-silver/30 rounded-lg text-white text-sm focus:border-luxury-gold focus:outline-none transition-colors duration-300"
                    placeholder="Seu nome"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-luxury-silver text-sm mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-silver/30 rounded-lg text-white text-sm focus:border-luxury-gold focus:outline-none transition-colors duration-300"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="assunto" className="block text-luxury-silver text-sm mb-2">
                    Assunto *
                  </label>
                  <select
                    id="assunto"
                    name="assunto"
                    value={formData.assunto}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-silver/30 rounded-lg text-white text-sm focus:border-luxury-gold focus:outline-none transition-colors duration-300 cursor-pointer"
                  >
                    <option value="">Selecione um assunto</option>
                    <option value="Informações sobre Cursos">Informações sobre Cursos</option>
                    <option value="Suporte Técnico">Suporte Técnico</option>
                    <option value="Parcerias">Parcerias</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-luxury-silver text-sm mb-2">
                    Mensagem * (máximo 500 caracteres)
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-luxury-black border border-luxury-silver/30 rounded-lg text-white text-sm focus:border-luxury-gold focus:outline-none transition-colors duration-300 resize-none"
                    placeholder="Escreva sua mensagem aqui..."
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {formData.mensagem.length}/500 caracteres
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.mensagem.length > 500}
                  className="w-full px-8 py-4 bg-gradient-gold text-black font-semibold rounded-full hover:shadow-gold transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
                    Mensagem enviada com sucesso! Entraremos em contacto em breve.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
                    Erro ao enviar mensagem. Por favor, tente novamente.
                  </div>
                )}
              </form>
            </motion.div>

            {/* Informações de Contacto */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Contactos Principais */}
              {outrosContactos.map((contacto, index) => (
                <div key={contacto.id} className="bg-gradient-dark rounded-2xl p-8 border border-luxury-gold/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-gold rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${getIconForType(contacto.tipo)} text-black text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-luxury-gold text-lg font-bold mb-2">{contacto.label}</h3>
                      {contacto.tipo === 'email' ? (
                        <a
                          href={`mailto:${contacto.valor}`}
                          className="text-gray-400 text-sm hover:text-luxury-gold transition-colors duration-300 cursor-pointer break-all"
                        >
                          {contacto.valor}
                        </a>
                      ) : contacto.tipo === 'telefone' ? (
                        <a
                          href={`tel:${contacto.valor}`}
                          className="text-gray-400 text-sm hover:text-luxury-gold transition-colors duration-300 cursor-pointer"
                        >
                          {contacto.valor}
                        </a>
                      ) : (
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {contacto.valor}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Horários de Funcionamento */}
              {horarios.length > 0 && (
                <div className="bg-gradient-dark rounded-2xl p-8 border border-luxury-gold/20">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-gold rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-black text-xl"></i>
                    </div>
                    <h3 className="text-luxury-gold text-lg font-bold">Horário de Atendimento</h3>
                  </div>
                  <div className="space-y-3">
                    {horarios.map((horario) => (
                      <div key={horario.id} className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">{horario.label}:</span>
                        <span className="text-white text-sm font-medium">{horario.valor}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Redes Sociais */}
              {redesSociais.length > 0 && (
                <div className="bg-gradient-dark rounded-2xl p-8 border border-luxury-gold/20">
                  <h3 className="text-luxury-gold text-lg font-bold mb-4">Redes Sociais</h3>
                  <div className="flex gap-4 flex-wrap">
                    {redesSociais.map((rede) => (
                      <a
                        key={rede.id}
                        href={rede.valor}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={rede.label}
                        className="w-12 h-12 rounded-full border-2 border-luxury-silver flex items-center justify-center text-luxury-silver hover:bg-luxury-gold hover:text-black hover:border-luxury-gold transition-all duration-300 cursor-pointer"
                      >
                        {renderSocialIcon(rede.tipo)}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Resposta Rápida */}
              <div className="bg-gradient-dark rounded-2xl p-8 border border-luxury-gold/20">
                <h3 className="text-luxury-gold text-lg font-bold mb-4">Resposta Rápida</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Respondemos a todas as mensagens em até 24 horas. Para questões urgentes, entre em contacto através do nosso email.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactosPage;
